/**
 * Bookings List Page Component
 * /admin/bookings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import apiClient from '../api/client';
import ExportMenu from '../components/ExportMenu';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [statusFilter, searchTerm, allBookings]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.getBookings({ search: searchTerm });
      // Handle paginated response
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setAllBookings(data);
      
      // Calculate stats
      const statsData = {
        total: data.length,
        pending: data.filter(b => b.status === 'pending').length,
        confirmed: data.filter(b => b.status === 'confirmed').length,
        completed: data.filter(b => b.status === 'completed').length,
        cancelled: data.filter(b => b.status === 'cancelled').length,
      };
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...allBookings];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(b => {
        const customerName = (b.customer_name || b.customer_email || '').toLowerCase();
        const vehicle = (b.vehicle_plate_number || b.vehicle || '').toLowerCase();
        const bookingNumber = (b.booking_number || b.id || '').toLowerCase();
        const serviceType = (b.service_type || '').toLowerCase();
        return (
          customerName.includes(searchLower) ||
          vehicle.includes(searchLower) ||
          bookingNumber.includes(searchLower) ||
          serviceType.includes(searchLower)
        );
      });
    }

    setBookings(filtered);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadBookings();
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleStatsCardClick = (status) => {
    setStatusFilter(status);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setSearchTerm('');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'info',
      completed: 'success',
      cancelled: 'error',
      in_progress: 'primary',
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading && bookings.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Bookings
        </Typography>
        <Box display="flex" gap={1}>
          <ExportMenu
            resource="bookings/bookings"
            params={{ search: searchTerm, status: statusFilter !== 'all' ? statusFilter : '' }}
            onError={setError}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/bookings/new')}
          >
            New Booking
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
              border: statusFilter === 'all' ? '2px solid' : 'none',
              borderColor: statusFilter === 'all' ? 'primary.main' : 'transparent'
            }}
            onClick={() => handleStatsCardClick('all')}
          >
            <CardContent>
              <Typography variant="h4" component="div">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Bookings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
              border: statusFilter === 'pending' ? '2px solid' : 'none',
              borderColor: statusFilter === 'pending' ? 'warning.main' : 'transparent'
            }}
            onClick={() => handleStatsCardClick('pending')}
          >
            <CardContent>
              <Typography variant="h4" component="div" color="warning.main">
                {stats.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
              border: statusFilter === 'confirmed' ? '2px solid' : 'none',
              borderColor: statusFilter === 'confirmed' ? 'info.main' : 'transparent'
            }}
            onClick={() => handleStatsCardClick('confirmed')}
          >
            <CardContent>
              <Typography variant="h4" component="div" color="info.main">
                {stats.confirmed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Confirmed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
              border: statusFilter === 'completed' ? '2px solid' : 'none',
              borderColor: statusFilter === 'completed' ? 'success.main' : 'transparent'
            }}
            onClick={() => handleStatsCardClick('completed')}
          >
            <CardContent>
              <Typography variant="h4" component="div" color="success.main">
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <Card 
            sx={{ 
              cursor: 'pointer', 
              transition: 'all 0.3s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
              border: statusFilter === 'cancelled' ? '2px solid' : 'none',
              borderColor: statusFilter === 'cancelled' ? 'error.main' : 'transparent'
            }}
            onClick={() => handleStatsCardClick('cancelled')}
          >
            <CardContent>
              <Typography variant="h4" component="div" color="error.main">
                {stats.cancelled}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cancelled
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                placeholder="Search bookings by customer, vehicle, driver..."
                value={searchTerm}
                onChange={handleSearch}
                slotProps={{ input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                } }}
              />
            </form>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            {(statusFilter !== 'all' || searchTerm) && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Booking ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Service Type</TableCell>
              <TableCell>Start Date/Time</TableCell>
              <TableCell>End Date/Time</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No bookings found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => (
                <TableRow key={booking.id} hover>
                  <TableCell>{booking.booking_number || booking.id.substring(0, 8)}</TableCell>
                  <TableCell>{booking.customer_name || booking.customer_email || '-'}</TableCell>
                  <TableCell>{booking.service_type || '-'}</TableCell>
                  <TableCell>{formatDate(booking.start_datetime)}</TableCell>
                  <TableCell>{formatDate(booking.end_datetime)}</TableCell>
                  <TableCell>
                    {booking.vehicle_plate_number || booking.vehicle || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={booking.status || 'pending'}
                      color={getStatusColor(booking.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/bookings/${booking.id}`)}
                      title="View"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/bookings/${booking.id}/edit`)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Bookings;

